import React, { useState, useMemo, useRef, useEffect } from "react";
import useSWR from 'swr';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import PackageCardAlpha from "./PackageCardAlpha";
import CtypeCheck from "./CtypeCheck";
import ArtformCheck from "./ArtformCheck";
import WorkshopSearch from "./WorkshopSearch";

const API_URL = `${process.env.REACT_APP_API_FOLDER}list_all_workshops/`;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;
const PAGE_SIZE = 15;
const showUpcoming = false;  // Toggle this to true to display the banner

const fetcher = url =>
  fetch(url, { headers: { 'X-API-TOKEN': API_TOKEN } })
    .then(res => {
      if (!res.ok) throw new Error(`Network response was not ok: ${res.status}`);
      return res.json();
    });

function WorkshopSidebarWrap() {
  // Data fetching via SWR
  const { data: allWorkshops = [], error, isLoading } = useSWR(API_URL, fetcher);

  // Local filter and UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [ctypeFilter, setCtypeFilter] = useState("all");
  const [artformFilters, setArtformFilters] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  // Track screen size to determine mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter logic
  const filtered = useMemo(() => {
    return allWorkshops.filter(w => {
      const text = `${w.title_with} ${w.tutors}`.toLowerCase();
      if (searchTerm && !text.includes(searchTerm.toLowerCase())) return false;
      if (ctypeFilter !== "all" && w.ctype !== ctypeFilter) return false;
      if (artformFilters.size) {
        const arts = w.artforms.split(",").map(s => s.trim().toLowerCase());
        if (![...artformFilters].some(af => arts.includes(af))) return false;
      }
      return true;
    });
  }, [allWorkshops, searchTerm, ctypeFilter, artformFilters]);

  // Pagination
  const [page, setPage] = useState(1);
  const loadMore = () => setPage(p => p + 1);
  useEffect(() => setPage(1), [filtered]);
  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadMore(),
      { rootMargin: "200px" }
    );
    if (sentinelRef.current) io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="package-sidebar-wrapper pt-110">
        <div className="container">
          <div className="row g-4">
            {Array(6).fill().map((_, idx) => (
              <div key={idx} className="col-lg-4 col-md-6 fadeffect">
                <Skeleton height={350} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) return <p className="text-danger">Error: {error.message}</p>;

  return (
    <div className="package-sidebar-wrapper pt-110">
      <div className="container">
        <div className="row">

          {/* Sidebar on mobile with toggle */}
          <div className="col-lg-3 mb-4">
            {isMobile ? (
              <>
                <button
                  className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                  onClick={() => setSidebarOpen(open => !open)}
                >
                  <span>{sidebarOpen ? "Hide filters" : "Search and filter"}</span>
                  <i className="bi bi-funnel" />
                </button>
                <div
                  className={`collapse-slide mt-3 ${sidebarOpen ? "open" : ""}`}
                  style={{
                    maxHeight: sidebarOpen ? "1000px" : "0",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease"
                  }}
                >
                  <div className="package-sidebar">
                    <WorkshopSearch value={searchTerm} onChange={setSearchTerm} />
                    <CtypeCheck value={ctypeFilter} onChange={setCtypeFilter} />
                    <ArtformCheck value={artformFilters} onChange={setArtformFilters} />
                  </div>
                </div>
              </>
            ) : (
              <div className="package-sidebar">
                <WorkshopSearch value={searchTerm} onChange={setSearchTerm} />
                <CtypeCheck value={ctypeFilter} onChange={setCtypeFilter} />
                <ArtformCheck value={artformFilters} onChange={setArtformFilters} />
              </div>
            )}
          </div>

          {/* Workshop cards with conditional promotional banner */}
          <div className="col-lg-9">
            {showUpcoming && (
              <div className="col-12 mb-4">
                <div
                  style={{
                    backgroundColor: 'lightblue',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center'
                  }}
                >
                  Looking for a sneak peak into what's coming up?{' '}
                  <a
                    href="https://www.campfr.com/upcoming"
                  >
                    Click here
                  </a>{' '}
                  to view workshops in progress!
                </div>
              </div>
            )}

            <div className="row g-4">
              {visible.map(w => (
                <div className="col-lg-4 col-md-6 fadeffect" key={w.id}>
                  <PackageCardAlpha workshop={w} />
                </div>
              ))}
            </div>

            <div ref={sentinelRef} />
            {hasMore && (
              <div className="text-center mt-4">
                <button onClick={loadMore} className="btn btn-primary">
                  Load more
                </button>
              </div>
            )}
            {!visible.length && <p>No workshops match your filters.</p>}
          </div>

        </div>
      </div>
    </div>
  );
}

export default WorkshopSidebarWrap;
